class MulticolumnVideoItem extends HTMLElement {
  constructor() {
    super();
    this.video = null;
    this.playButton = null;
    this.isPreviewPlaying = false;
    this.previewTimeout = null;
    this.previewDuration = parseInt(this.dataset.previewDuration) || 1500;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.video = this.querySelector('video');
    this.playButton = this.querySelector('.video-play-button');

    if (!this.video || !this.playButton) return;

    // Preview functionality
    this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    // Play button click handler
    this.playButton.addEventListener('click', this.handlePlayButtonClick.bind(this));

    // Video event handlers
    this.video.addEventListener('pause', this.handleVideoPause.bind(this));
    this.video.addEventListener('play', this.handleVideoPlay.bind(this));
    this.video.addEventListener('ended', this.handleVideoEnded.bind(this));
  }

  handleMouseEnter() {
    if (!this.classList.contains('playing')) {
      this.isPreviewPlaying = true;
      this.video.currentTime = 0;
      this.video.play();
      this.classList.add('previewing');

      // Set timeout to pause video after preview duration
      this.previewTimeout = setTimeout(() => {
        if (this.isPreviewPlaying && !this.classList.contains('playing')) {
          this.video.pause();
          this.video.currentTime = 0;
          this.classList.remove('previewing');
          this.isPreviewPlaying = false;
        }
      }, this.previewDuration);
    }
  }

  handleMouseLeave() {
    if (this.isPreviewPlaying && !this.classList.contains('playing')) {
      clearTimeout(this.previewTimeout);
      this.video.pause();
      this.video.currentTime = 0;
      this.classList.remove('previewing');
      this.isPreviewPlaying = false;
    }
  }

  handlePlayButtonClick(e) {
    e.stopPropagation();
    this.clearPreviewState();
    
    if (this.video.paused) {
      this.video.play();
      this.classList.add('playing');
    } else {
      this.video.pause();
      this.classList.remove('playing');
    }
  }

  handleVideoPause() {
    if (!this.isPreviewPlaying) {
      this.classList.remove('playing');
    }
  }

  handleVideoPlay() {
    if (!this.isPreviewPlaying) {
      this.classList.add('playing');
    }
  }

  handleVideoEnded() {
    if (this.isPreviewPlaying) {
      this.clearPreviewState();
    }
  }

  clearPreviewState() {
    clearTimeout(this.previewTimeout);
    this.isPreviewPlaying = false;
    this.classList.remove('previewing');
  }

  cleanup() {
    if (this.video && this.playButton) {
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.playButton.removeEventListener('click', this.handlePlayButtonClick);
      this.video.removeEventListener('pause', this.handleVideoPause);
      this.video.removeEventListener('play', this.handleVideoPlay);
      this.video.removeEventListener('ended', this.handleVideoEnded);
      this.clearPreviewState();
    }
  }
}

// Register the custom element
if (!customElements.get('multicolumn-video-item')) {
  customElements.define('multicolumn-video-item', MulticolumnVideoItem);
}

// Multicolumn Auto-scroll Component
class MulticolumnAutoscroll extends HTMLElement {
  constructor() {
    super();

    this.config = {
      moveTime: parseFloat(this.dataset.speed || 1.6), // Time for 100px to move
      space: 100, // 100px base distance
    };

    this.list = this.querySelector('.multicolumn-list');
    this.initialized = false;

    // Initialize when visible (similar to scrolling-promotion)
    if (window.theme && window.theme.initWhenVisible) {
      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 600
      });
    } else {
      // Fallback to immediate initialization
      this.init();
    }

    // Listen for window resize to handle mobile/desktop transitions
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  init() {
    if (!this.list) return;

    // ONLY initialize on mobile screens - desktop should be completely unaffected
    if (window.innerWidth <= 749) {
      this.initMobileScroll();
    }
    // On desktop, do nothing - let the normal grid layout work
  }

  initMobileScroll() {
    // Only initialize if there's content to scroll and not already initialized
    if (this.list && this.list.children.length > 0 && !this.initialized) {
      this.list.classList.add('multicolumn-list--animated');

      // No cloning - just show original items
      this.initialized = true;

      // Calculate animation duration based on content width
      const animationTimeFrame = (this.list.clientWidth / this.config.space) * this.config.moveTime;
      this.style.setProperty('--duration', `${animationTimeFrame}s`);

      // Pause when out of view to save resources
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.scrollingPlay();
          } else {
            this.scrollingPause();
          }
        });
      }, { rootMargin: '0px 0px 50px 0px' });

      observer.observe(this);
    }
  }

  handleResize() {
    // Handle transitions between mobile and desktop
    if (window.innerWidth <= 749 && !this.initialized) {
      // Transitioning to mobile - initialize scroll
      this.initMobileScroll();
    } else if (window.innerWidth > 749 && this.initialized) {
      // Transitioning to desktop - remove animation class
      if (this.list) {
        this.list.classList.remove('multicolumn-list--animated');
      }
    }
  }

  scrollingPlay() {
    this.classList.remove('multicolumn-autoscroll--paused');
  }

  scrollingPause() {
    this.classList.add('multicolumn-autoscroll--paused');
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }
}

// Register the auto-scroll custom element
if (!customElements.get('multicolumn-autoscroll')) {
  customElements.define('multicolumn-autoscroll', MulticolumnAutoscroll);
}